/**
 * Question Coach English wording (DKPLAT-812).
 *
 * The Pick'em Question Coach auto-generates the English text of stat-based questions. Built
 * generically from the feed's stat display name, NFL questions read badly ("Which player will
 * have the most Rushing Longest?", "Will X have over 25.5 Field Goal Longest?"), so DK asked for
 * specific wording per stat.
 *
 * Why a map rather than renaming stats: DK's wording changes the verb and the sentence shape
 * ("make the most Field Goals", "record a Reception longer than 5.5 yards"), which no stat name can
 * produce. The feed's stat display names are also shared with Bingo, which only just had them
 * tidied (#920, DKPLAT-818), so they must not be bent to suit the Coach.
 *
 * - NFL and NCAAFB share one map: NCAAFB uses the NFL stat list in the feed.
 * - All 18 Coach gridiron codes x 4 question types (72 pairs) are listed, even the ones whose text
 *   matches today's generic output, so a future feed rename can't silently change Coach wording.
 * - Any sport / stat / question type not in the map falls back to the generic text, which is
 *   exactly what the backend factories have always produced.
 * - French is deliberately not covered: `questionTextFr` stays on the backend's generic builders.
 *
 * Both the backend question factories (saved text) and the admin Coach review screen (preview)
 * read this module, so the preview and the saved question can't drift apart.
 */

export type CoachQuestionType =
  | 'player_selection'
  | 'team_selection'
  | 'player_over_under'
  | 'team_over_under';

export const COACH_QUESTION_TYPES: readonly CoachQuestionType[] = [
  'player_selection',
  'team_selection',
  'player_over_under',
  'team_over_under',
];

/** Templates per stat code. `{entity}` = player display name / team subject; `{line}` = admin's line. */
export type CoachWordingMap = Readonly<Record<string, Readonly<Record<CoachQuestionType, string>>>>;

/** Shorthand for the common "have the most X" / "have over N X" shape. */
function countWording(statNoun: string): Readonly<Record<CoachQuestionType, string>> {
  return {
    player_selection: `Which player will have the most ${statNoun}?`,
    team_selection: `Which team will have the most ${statNoun}?`,
    player_over_under: `Will {entity} have over {line} ${statNoun}?`,
    team_over_under: `Will {entity} have over {line} ${statNoun}?`,
  };
}

export const GRIDIRON_COACH_QUESTION_WORDING_EN: CoachWordingMap = {
  // Passing
  pass_attempts: countWording('Passing Attempts'),
  pass_completions: countWording('Passing Completions'),
  pass_yards: countWording('Passing Yards'),
  pass_touchdowns: countWording('Passing Touchdowns'),
  pass_interceptions: {
    player_selection: 'Which player will throw the most Interceptions?',
    team_selection: 'Which team will throw the most Interceptions?',
    player_over_under: 'Will {entity} throw over {line} Interceptions?',
    team_over_under: 'Will {entity} throw over {line} Interceptions?',
  },

  // Rushing
  rush_attempts: countWording('Rushing Attempts'),
  rush_touchdowns: countWording('Rushing Touchdowns'),
  rush_yards: countWording('Rushing Yards'),
  rush_longest: {
    player_selection: 'Which player will have the longest Rush?',
    team_selection: 'Which team will have the longest Rush?',
    player_over_under: 'Will {entity} record a Rush longer than {line} yards?',
    team_over_under: 'Will {entity} record a Rush longer than {line} yards?',
  },

  // Receiving
  receive_receptions: countWording('Receptions'),
  receive_targets: {
    player_selection: 'Which player will receive the most pass Targets?',
    team_selection: 'Which team will have the most pass Targets?',
    player_over_under: 'Will {entity} receive over {line} pass Targets?',
    team_over_under: 'Will {entity} receive over {line} pass Targets?',
  },
  receive_yards: countWording('Receiving Yards'),
  receive_longest: {
    player_selection: 'Which player will have the longest Reception?',
    team_selection: 'Which team will have the longest Reception?',
    player_over_under: 'Will {entity} record a Reception longer than {line} yards?',
    team_over_under: 'Will {entity} record a Reception longer than {line} yards?',
  },
  receive_touchdowns: countWording('Receiving Touchdowns'),

  // Defence
  defense_sacks: countWording('Sacks'),
  defense_interceptions: countWording('Interceptions'),

  // Kicking
  field_goal_made: {
    player_selection: 'Which player will make the most Field Goals?',
    team_selection: 'Which team will make the most Field Goals?',
    player_over_under: 'Will {entity} have over {line} Field Goals Made?',
    team_over_under: 'Will {entity} have over {line} Field Goals Made?',
  },
  field_goal_longest: {
    player_selection: 'Which player will make the longest Field Goal?',
    team_selection: 'Which team will make the longest Field Goal?',
    player_over_under: 'Will {entity} make a Field Goal longer than {line} yards?',
    team_over_under: 'Will {entity} make a Field Goal longer than {line} yards?',
  },
};

/** Tennis set winners: a two-player pick reads as a question about the set, not a count. */
export const TENNIS_COACH_QUESTION_WORDING_EN: CoachWordingMap = Object.fromEntries(
  ['1st', '2nd', '3rd', '4th', '5th'].map((ordinal, i) => [
    `tennis_set_${i + 1}_won`,
    {
      player_selection: `Which player will win the ${ordinal} set?`,
      team_selection: `Which player will win the ${ordinal} set?`,
      player_over_under: `Will {entity} win the ${ordinal} set?`,
      team_over_under: `Will {entity} win the ${ordinal} set?`,
    },
  ])
);

const WORDING_BY_SPORT: Readonly<Record<string, CoachWordingMap>> = {
  nfl: GRIDIRON_COACH_QUESTION_WORDING_EN,
  ncaafb: GRIDIRON_COACH_QUESTION_WORDING_EN, // NCAAFB shares NFL_STAT_TYPES in the feed
  tennis: TENNIS_COACH_QUESTION_WORDING_EN,
};

/**
 * NFL `team.name` is the nickname ("Chiefs") so it needs "the" ("Will the Chiefs…");
 * NCAAFB `team.name` is the school ("North Carolina") so it takes no article.
 */
const SPORTS_WITH_TEAM_ARTICLE: ReadonlySet<string> = new Set(['nfl']);

export interface CoachQuestionTextInput {
  sport?: string | null;
  statTypeCode?: string | null;
  /** Feed display name — used by the generic fallback only. */
  statTypeName: string;
  questionType: CoachQuestionType;
  /** Over/under only: the player display name or team name. */
  entityName?: string;
  /** Over/under only: the admin's line. */
  line?: number | string;
}

/** The mapped English template for a pair, or `undefined` when the pair isn't mapped. */
export function getCoachQuestionTemplateEn(
  sport: string | null | undefined,
  statTypeCode: string | null | undefined,
  questionType: CoachQuestionType,
): string | undefined {
  if (!sport || !statTypeCode) return undefined;
  const map = WORDING_BY_SPORT[sport.toLowerCase()];
  if (!map || !Object.prototype.hasOwnProperty.call(map, statTypeCode)) return undefined;
  return map[statTypeCode][questionType];
}

/** Exactly the backend's historic generic English text (no lower-casing, no article). */
export function genericCoachQuestionTextEn(input: CoachQuestionTextInput): string {
  switch (input.questionType) {
    case 'player_selection':
      return `Which player will have the most ${input.statTypeName}?`;
    case 'team_selection':
      return `Which team will have the most ${input.statTypeName}?`;
    default:
      return `Will ${input.entityName} have over ${input.line} ${input.statTypeName}?`;
  }
}

/** English question text: the mapped template when one exists, otherwise the generic text. */
export function buildCoachQuestionTextEn(input: CoachQuestionTextInput): string {
  const template = getCoachQuestionTemplateEn(input.sport, input.statTypeCode, input.questionType);
  if (template === undefined) return genericCoachQuestionTextEn(input);

  let entity = input.entityName ?? '';
  if (
    input.questionType === 'team_over_under' &&
    input.sport &&
    SPORTS_WITH_TEAM_ARTICLE.has(input.sport.toLowerCase()) &&
    entity &&
    !/^the\s/i.test(entity)
  ) {
    entity = `the ${entity}`;
  }

  return template
    .split('{entity}')
    .join(entity)
    .split('{line}')
    .join(input.line === undefined ? '' : String(input.line));
}
